import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { directoryGridOrder } from 'src/app/helper/sortingHelper';
import { GridSettings } from 'src/app/samwebDescription/samweb/dashboard/grid-column-settings';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import { ShortcutTypeDirectory } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeDirectory';
import { BrickGridsState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brick-grids-state/brick-grids.state';
// tslint:disable: max-line-length
import { ClearAndStartLoadingDirectoryValues } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-browser-behaviour/directory-browser-behaviour.actions';
import { DirectoryOrganisationDataSelectors } from 'src/app/store/samwin/directory/directory-organisation-data/directory-organisation-data.selectors';
import { DirectoryStructureSelectors } from 'src/app/store/samwin/directory/directory-structure/directory-structure.selectors';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';
import {
   BooleanOrgField,
   BrickSettingsService,
   DateTimeOrgField,
   DirectoryBrickSettingRemoteDto,
   DirectoryEntryRemoteDto,
   Int32OrgField,
   OrganisationEntryRemoteDto,
   StringOrgField,
} from 'src/generated/swagger';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import {
   ChangeAutoSearchColumn,
   ChangeAutomaticFilterOnSkillOrganisationAssociations,
   ChangeAutomaticRestrictForIncomingCaller,
   ChangeClearSearchBoxOnIncomingCall,
   ChangeDefaultDirectoryId,
   ChangeDirectoryCallableColumns,
   ChangeDirectoryDefaultCallColumn,
   ChangeDirectoryDefaultSortColumn,
   ChangeDirectorySearchableColumns,
   ChangeDirectorySearchablePhoneticColumns,
   ChangeMinLengthToStartFiltering,
   ChangeRetainFilterOnCallFinish,
   ChangeSchedulerSelectedView,
   ChangeSchedulerShowBusinessHours,
   ChangeStartNewCallOnDoubleClick,
   ChangedHotkeyCombo,
} from './directory-settings.actions';

export interface DirectorySettingsStateModel extends IBrickSettingStateModel<DirectoryBrickSettingRemoteDto> {}

function getDefaultSpecificBrickSetting(): DirectoryBrickSettingRemoteDto {
   return {
      automaticFilterOnSkillOrganisationAssociations: true,
      automaticRestrictForIncomingCaller: false,
      retainFilterOnCallFinish: true,
      startNewCallOnDoubleClick: true,
      clearSearchBoxOnIncomingCall: false,
      minLengthToStartFiltering: 0,
      searchableColumnKeys: null,
      searchablePhoneticColumnKeys: null,
      defaultCallColumnKey: 'tbl_org_00_fld_orgnum',
      callableColumnKeys: ['tbl_org_00_fld_orgnum', 'tbl_org_00_fld_sms', 'tbl_org_00_fld_altnst'],
      commonBrickSettingRemoteDto: null,
      defaultSortColumnKey: 'tbl_org_00_fld_orgnam',
      defaultSelectedDirectoryId: DirectoryOrganisationDataSelectors.NO_RESTRICTION_ENTRY.id,
      // NOTE: should not be required, maybe data is not loaded correctly?
      hotkeyCombos: _(ShortcutTypeDirectory.values())
         .map((directorShortCut) => {
            return { [directorShortCut.enumName]: directorShortCut.defaultHotkeyCombo };
         })
         .head(),
      schedulerSelectedView: 0,
      schedulerShowBusinessHours: false,
      autoSearchColumnKey: null,
   };
}

@State<DirectorySettingsStateModel>({
   name: 'directorySettings',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
   },
})
@Injectable()
export class DirectorySettingsState extends SpecificBrickSettingsStateBase<DirectoryBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([DirectorySettingsState, DirectorySettingsState.getSettingModelFn])
   public static getSettingState(state: DirectorySettingsState, getSettingModel: (state, brickInstanceId) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModel(state, brickId);
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getAutomaticFilterOnSkillOrganisationAssociations(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).automaticFilterOnSkillOrganisationAssociations;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getAutomaticRestrictForIncomingCaller(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).automaticRestrictForIncomingCaller;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getClearSearchBoxOnIncomingCall(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).clearSearchBoxOnIncomingCall;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getRetainFilterOnCallFinish(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).retainFilterOnCallFinish;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getStartNewCallOnDoubleClick(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).startNewCallOnDoubleClick;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getMinLengthToStartFiltering(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).minLengthToStartFiltering;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getSearchableColumnKeys(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).searchableColumnKeys;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getAutoSearchColumnKey(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).autoSearchColumnKey;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getDefaultSelectedDirectoryId(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).defaultSelectedDirectoryId;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getDefaultSortColumnKey(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).defaultSortColumnKey;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getCommonBrickSettingRemoteDto(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).commonBrickSettingRemoteDto;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getCallableColumnKeys(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).callableColumnKeys;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getDefaultCallColumnKey(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).defaultCallColumnKey;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static searchablePhoneticColumnKeys(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).searchablePhoneticColumnKeys;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getSchedulerSelectedView(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).schedulerSelectedView;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getSchedulerShowBusinessHours(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).schedulerShowBusinessHours;
      };
   }

   @Selector([DirectorySettingsState.getSettingState])
   public static getHotkeySettings(getSettingState: (brickId: string) => DirectoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingState(brickId).hotkeyCombos;
      };
   }

   @Selector([DirectorySettingsState.getHotkeySettings])
   public static getHotkeyForAction(
      hotkeysetting: (
         brickId: string
      ) => {
         [key: string]: string;
      }
   ) {
      return (brickId: string, actionName: string) => {
         return hotkeysetting(brickId)[actionName] ?? ShortcutTypeDirectory[actionName].defaultHotkeyCombo;
      };
   }

   @Selector([DirectorySettingsState.getHotkeySettings])
   public static getHotkeySettingForHotkeyType(
      hotkeysetting: (
         brickId: string
      ) => {
         [key: string]: string;
      }
   ) {
      return (brickId: string, hotkeyTyp: ShortcutTypeDirectory) => {
         return hotkeysetting(brickId)[hotkeyTyp.value];
      };
   }

   @Selector([DirectorySettingsState.getHotkeySettingForHotkeyType])
   public static getEffectiveHotkeySettingForHotkeyType(getHotkey: (brickId: string, hotkeyTyp: ShortcutTypeDirectory) => string) {
      return (brickId: string, hotkeyTyp: ShortcutTypeDirectory) => {
         const combo = getHotkey(brickId, hotkeyTyp.value);
         if (_.isNil(combo)) {
            return hotkeyTyp.defaultHotkeyCombo;
         }
         return combo;
      };
   }

   @Selector([DirectoryStructureState.availableDirectoryColumns])
   public static getAllPossibleSearchableColumns(availableColumns: DirectoryColumn[]): DirectoryColumn[] {
      return availableColumns.filter((x) => x.columnTyp.isValueSearchable);
   }

   @Selector([DirectoryStructureState.availablePhoneticDirectoryColumns])
   public static getAllPossibleSearchablePhoneticColumns(availableColumns: DirectoryColumn[]): DirectoryColumn[] {
      return availableColumns.filter((x) => x.columnTyp.isValueSearchable);
   }

   @Selector([DirectorySettingsState])
   public static areSettingsFromServerReceived(state: DirectorySettingsStateModel): boolean {
      return state.settingsFromServerReceived;
   }

   @Selector([DirectorySettingsState.getCallableColumnKeys, DirectoryStructureState.availableDirectoryColumns])
   public static getCallableColumns(
      getCallableColumnKeys: (brickId: string) => string[],
      availableColumns: DirectoryColumn[]
   ): (brickId: string) => DirectoryColumn[] {
      return (brickId: string) => {
         let callableColumnKeys = getCallableColumnKeys(brickId);
         if (_.isNil(callableColumnKeys) || _.isEmpty(callableColumnKeys)) {
            callableColumnKeys = [getDefaultSpecificBrickSetting().defaultCallColumnKey];
         }
         const callableColumns = _(callableColumnKeys)
            .map((columnKey) =>
               _(availableColumns)
                  .filter((x) => x.directoryColumnKey === columnKey)
                  .head()
            )
            .filter((x) => !_.isNil(x))
            .value();
         return callableColumns;
      };
   }

   @Selector([DirectorySettingsState.getDefaultCallColumnKey, DirectoryStructureState.availableDirectoryColumns])
   public static getDefaultCallColumn(
      getDefaultCallColumnKey: (brickId: string) => string,
      availableColumns: DirectoryColumn[]
   ): (brickId: string) => DirectoryColumn {
      return (brickId: string) => {
         const defaultCallColumnKey = getDefaultCallColumnKey(brickId);
         let defaultCallColumn: DirectoryColumn = _(availableColumns)
            .filter((x) => x.directoryColumnKey === defaultCallColumnKey)
            .head();
         if (_.isNil(defaultCallColumn)) {
            defaultCallColumn = _(availableColumns)
               .filter((x) => x.directoryColumnKey === getDefaultSpecificBrickSetting().defaultCallColumnKey)
               .head();
         }
         return defaultCallColumn;
      };
   }

   @Selector([DirectorySettingsState.getCallableColumns, DirectoryStructureState.editableColumns, BrickGridsState.getBrickColumnConfigurationForDirectoryBrick])
   public static getCallableColumnsInOrder(
      callableColumns: (brickId: string) => DirectoryColumn[],
      editableColumns: DirectoryColumn[],
      getBrickColumnConfiguration: (brickInstanceId: string, gridId: string) => GridSettings
   ): (brickId: string) => DirectoryColumn[] {
      return (brickId: string) => {
         const setting = getBrickColumnConfiguration(brickId, 'default');
         return directoryGridOrder(editableColumns, setting)
            .filter((dir) => dir.organisationUnitNumber === '00')
            .filter((dir) => callableColumns(brickId).includes(dir));
      };
   }

   @Selector([DirectorySettingsState.getCallableColumnsInOrder, DirectorySettingsState.getDefaultCallColumn])
   public static getDefaultOrCallableColumnsInOrder(
      callableColumns: (brickId: string) => DirectoryColumn[],
      getDefaultCallColumn: (brickId: string) => DirectoryColumn
   ): (brickId: string) => DirectoryColumn[] {
      return (brickId: string) => {
         const defaultCallColumn = getDefaultCallColumn(brickId);
         return [defaultCallColumn, ...callableColumns(brickId).filter((dir) => defaultCallColumn !== dir)];
      };
   }

   @Selector([DirectorySettingsState.getDefaultOrCallableColumnsInOrder, DirectoryStructureSelectors.getColumnInstance])
   public static getDefaultCallColumnWithValue(
      callableColumns: (brickId: string) => DirectoryColumn[],
      columnInstance: (
         organisationEntryRemoteDto: OrganisationEntryRemoteDto,
         directoryColumn: DirectoryColumn
      ) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField
   ): (brickId: string, entry: DirectoryEntryRemoteDto) => DirectoryColumn {
      return (brickId: string, entry: DirectoryEntryRemoteDto) => {
         return _(callableColumns(brickId))
            .filter((col) => !_.isNil(columnInstance(entry, col)?.value) && !_.isEmpty(columnInstance(entry, col)?.value))
            .head();
      };
   }

   @Selector([DirectorySettingsState.getDefaultOrCallableColumnsInOrder, DirectoryStructureSelectors.getColumnInstance])
   public static getSelectedAvailableCallColumn(
      callableColumns: (brickId: string) => DirectoryColumn[],
      columnInstance: (
         organisationEntryRemoteDto: OrganisationEntryRemoteDto,
         directoryColumn: DirectoryColumn
      ) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField
   ): (brickId: string, dataItem: DirectoryEntryRemoteDto, columnFieldId: string) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField {
      return (brickId: string, dataItem: DirectoryEntryRemoteDto, columnFieldId: string) => {
         return _(callableColumns(brickId))
            .filter((x) => x.directoryColumnKey === columnFieldId)
            .map((col) => columnInstance(dataItem, col))
            .filter((col) => !_.isNil(col?.value) && !_.isEmpty(col?.value))
            .head();
      };
   }

   @Selector([DirectorySettingsState.getDefaultOrCallableColumnsInOrder, DirectoryStructureSelectors.getColumnInstance])
   public static getDefaultOrAvailableCallColumn(
      callableColumns: (brickId: string) => DirectoryColumn[],
      columnInstance: (
         organisationEntryRemoteDto: OrganisationEntryRemoteDto,
         directoryColumn: DirectoryColumn
      ) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField
   ): (brickId: string, dataItem: DirectoryEntryRemoteDto) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField {
      return (brickId: string, dataItem: DirectoryEntryRemoteDto) => {
         return _(callableColumns(brickId))
            .map((col) => columnInstance(dataItem, col))
            .filter((col) => !_.isNil(col?.value) && !_.isEmpty(col?.value?.toString().trim()))
            .head();
      };
   }

   @Selector([DirectorySettingsState.getSelectedAvailableCallColumn, DirectorySettingsState.getDefaultOrAvailableCallColumn])
   public static getSelectedOrDefaultCallColumn(
      getSelectedAvailableCallColumn: (
         brickId: string,
         dataItem: DirectoryEntryRemoteDto,
         columnFieldId: string
      ) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField,
      getDefaultOrAvailableCallColumn: (
         brickId: string,
         dataItem: DirectoryEntryRemoteDto
      ) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField
   ): (brickId: string, dataItem: DirectoryEntryRemoteDto, columnFieldId: string) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField {
      return (brickId: string, dataItem: DirectoryEntryRemoteDto, columnFieldId: string) => {
         let rowContent = getSelectedAvailableCallColumn(brickId, dataItem, columnFieldId);
         if (_.isNil(rowContent?.value) || _.isEmpty(rowContent?.value?.toString().trim())) {
            rowContent = getDefaultOrAvailableCallColumn(brickId, dataItem);
         }
         return rowContent;
      };
   }

   @Selector([DirectorySettingsState.getCallableColumnsInOrder])
   public static getAvailableCallColumnKeys(callableColumns: (brickId: string) => DirectoryColumn[]): (brickId: string) => string[] {
      return (brickId: string) => {
         return callableColumns(brickId).map((x) => x.directoryColumnKey);
      };
   }

   @Selector([DirectorySettingsState.getDefaultSortColumnKey, DirectoryStructureState.availableDirectoryColumns])
   public static getDefaultSortColumn(
      getDefaultSortColumnKey: (brickId: string) => string,
      availableColumns: DirectoryColumn[]
   ): (brickId: string) => DirectoryColumn {
      return (brickId: string) => {
         const defaultSortColumnKey = getDefaultSortColumnKey(brickId);
         return _(availableColumns)
            .filter((x) => x.directoryColumnKey === defaultSortColumnKey)
            .head();
      };
   }

   @Selector([DirectorySettingsState.getAutoSearchColumnKey, DirectoryStructureState.availableDirectoryColumns])
   public static getDefaultListeningColumn(
      getAutoSearchColumnKey: (brickId: string) => string,
      availableColumns: DirectoryColumn[]
   ): (brickId: string) => DirectoryColumn {
      return (brickId: string) => {
         const defaultListeningColumnKey = getAutoSearchColumnKey(brickId);
         return _(availableColumns)
            .filter((x) => x.directoryColumnKey === defaultListeningColumnKey)
            .head();
      };
   }

   @Selector([
      DirectorySettingsState.areSettingsFromServerReceived,
      DirectorySettingsState.getDefaultSelectedDirectoryId,
      DirectoryOrganisationDataSelectors.getDirectoryRestrictionEntries,
   ])
   public static getDefaultSelectedDirectory(
      settingsFromServerReceived: boolean,
      getDefaultSelectedDirectoryId: (brickId: string) => number,
      directoryStructureData: OrganisationEntryRemoteDto[]
   ): (brickId: string) => OrganisationEntryRemoteDto {
      return (brickId: string) => {
         if (!settingsFromServerReceived) {
            return null;
         }
         const selectedDirectoryId = getDefaultSelectedDirectoryId(brickId);
         return _(directoryStructureData)
            .filter((x) => x.id === selectedDirectoryId)
            .head();
      };
   }

   @Selector([
      DirectorySettingsState.getSearchableColumnKeys,
      DirectoryStructureState.availableDirectoryColumns,
      DirectorySettingsState.getAllPossibleSearchableColumns,
   ])
   public static getDirectorySearchableColumns(
      getSearchableColumnKeys: (brickId) => string[],
      availableColumns: DirectoryColumn[],
      allAvailableDirectoryColumn: DirectoryColumn[]
   ): (brickId: string) => DirectoryColumn[] {
      return (brickId: string) => {
         let searchableColumnKeys = getSearchableColumnKeys(brickId);
         if (_.isNil(searchableColumnKeys) || _.isEmpty(searchableColumnKeys)) {
            searchableColumnKeys = allAvailableDirectoryColumn.filter((x) => x.organisationUnitNumber === '00').map((x) => x.directoryColumnKey);
         }
         const searchableColumns = _(searchableColumnKeys)
            .map((columnKey) =>
               _(availableColumns)
                  .filter((x) => x.directoryColumnKey === columnKey)
                  .head()
            )
            .filter((x) => !_.isNil(x))
            .value();
         return searchableColumns;
      };
   }

   @Selector([DirectorySettingsState.getDirectorySearchableColumns])
   public static getDirectorySearchableColumnKeys(getSearchableColumns: (brickId: string) => DirectoryColumn[]): (brickId: string) => string[] {
      return (brickId: string) => {
         const columns = getSearchableColumns(brickId);
         return columns?.map((column) => column.directoryColumnKey) ?? [];
      };
   }

   @Selector([
      DirectorySettingsState.searchablePhoneticColumnKeys,
      DirectoryStructureState.availableDirectoryColumns,
      DirectorySettingsState.getAllPossibleSearchableColumns,
   ])
   public static getDirectorySearchablePhoneticColumns(
      getSearchablePhoneticColumnKeys: (brickId: string) => string[],
      availableColumns: DirectoryColumn[],
      allAvailableDirectoryColumn: DirectoryColumn[]
   ): (brickId: string) => DirectoryColumn[] {
      return (brickId: string) => {
         let searchableColumnKeys = getSearchablePhoneticColumnKeys(brickId);
         if (_.isNil(searchableColumnKeys) || _.isEmpty(searchableColumnKeys)) {
            searchableColumnKeys = allAvailableDirectoryColumn.filter((x) => x.directoryColumnKey === 'tbl_org_00_fld_orgnam').map((x) => x.directoryColumnKey);
         }
         const searchableColumns = _(searchableColumnKeys)
            .map((columnKey) =>
               _(availableColumns)
                  .filter((x) => x.directoryColumnKey === columnKey)
                  .head()
            )
            .filter((x) => !_.isNil(x))
            .value();
         return searchableColumns;
      };
   }

   @Action(ChangedHotkeyCombo)
   @ImmutableContext()
   public changedHotkeyCombo(ctx: StateContext<DirectorySettingsStateModel>, action: ChangedHotkeyCombo) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.hotkeyCombos[action.shortcutType.value] = action.combo;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeAutomaticFilterOnSkillOrganisationAssociations)
   @ImmutableContext()
   public changeAutomaticFilteringOnIncomingCall(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeAutomaticFilterOnSkillOrganisationAssociations) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.automaticFilterOnSkillOrganisationAssociations = action.isFilterEnabled;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeAutomaticRestrictForIncomingCaller)
   @ImmutableContext()
   public changeAutomaticRestrictForIncomingCaller(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeAutomaticRestrictForIncomingCaller) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.automaticRestrictForIncomingCaller = action.isFilterEnabled;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeMinLengthToStartFiltering)
   @ImmutableContext()
   public changeMinLengthToStartFiltering(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeMinLengthToStartFiltering) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.minLengthToStartFiltering = action.minLengthToStartFiltering;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeRetainFilterOnCallFinish)
   @ImmutableContext()
   public changeRetainFilterOnCallFinish(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeRetainFilterOnCallFinish) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.retainFilterOnCallFinish = action.retainFilterOnCallFinish;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeDirectorySearchableColumns)
   public changeDirectorySearchableColumns(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeDirectorySearchableColumns) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.searchableColumnKeys = action.searchableColumnKeys;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeAutoSearchColumn)
   @ImmutableContext()
   public changeAutoSearchColumn(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeAutoSearchColumn) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.autoSearchColumnKey = action.searchingColumnKey;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeDirectorySearchablePhoneticColumns)
   public changeDirectorySearchablePhoneticColumns(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeDirectorySearchablePhoneticColumns) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.searchablePhoneticColumnKeys = action.searchableColumnKeys;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeDirectoryCallableColumns)
   public changeDirectoryCallableColumns(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeDirectoryCallableColumns) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.callableColumnKeys = action.callableColumnKeys;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return draft;
      });
      ctx.setState(state);
   }

   @Action(ChangeDirectoryDefaultCallColumn)
   @ImmutableContext()
   public ChangeDirectoryDefaultCallColumn(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeDirectoryDefaultCallColumn) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.defaultCallColumnKey = action.defaultCallColumnKey;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeDirectoryDefaultSortColumn)
   @ImmutableContext()
   public changeDirectoryDefaultSortColumn(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeDirectoryDefaultSortColumn) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.defaultSortColumnKey = action.defaultSortColumnKey;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
      return this.store.dispatch(new ClearAndStartLoadingDirectoryValues(action.brickInstanceId));
   }

   @Action(ChangeStartNewCallOnDoubleClick)
   @ImmutableContext()
   public changeStartNewCallOnDoubleClick(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeStartNewCallOnDoubleClick) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.startNewCallOnDoubleClick = action.startNewCallOnDoubleClick;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeClearSearchBoxOnIncomingCall)
   @ImmutableContext()
   public changeClearSearchBoxOnIncomingCall(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeClearSearchBoxOnIncomingCall) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.clearSearchBoxOnIncomingCall = action.clearSearchBoxOnIncomingCall;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeDefaultDirectoryId)
   @ImmutableContext()
   public changeDefaultDirectoryId(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeDefaultDirectoryId) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.defaultSelectedDirectoryId = action.defaultDirectoryId;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
      return this.store.dispatch(new ClearAndStartLoadingDirectoryValues(action.brickInstanceId));
   }

   @Action(ChangeSchedulerSelectedView)
   @ImmutableContext()
   public changeSchedulerSelectedView(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeSchedulerSelectedView) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.schedulerSelectedView = action.viewId;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeSchedulerShowBusinessHours)
   @ImmutableContext()
   public changeSchedulerShowBusinessHours(ctx: StateContext<DirectorySettingsStateModel>, action: ChangeSchedulerShowBusinessHours) {
      ctx.setState((state: DirectorySettingsStateModel) => {
         const brickSettingModel = DirectorySettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.schedulerShowBusinessHours = action.checked;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: DirectoryBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeDirectoryBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }
}
